import { devWarn, devError, devInfo } from "@/lib/logger";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TEST_LAUNCH_MODE, SUPPORT_EMAIL } from "@/lib/config";
import { Countdown } from "@/components/Countdown";
import { ReferralStatsWidget } from "@/components/dashboard/ReferralStatsWidget";
import { ReferralChartWidget } from "@/components/dashboard/ReferralChartWidget";
import { PushPermissionNudge } from "@/components/dashboard/PushPermissionNudge";
import InvoicesWidget from "@/components/InvoicesWidget";
import MarketplaceWidget from "@/components/MarketplaceWidget";
import MicButton from "@/components/MicButton";
import ListenButton from "@/components/ListenButton";
import MockupButton from "@/components/MockupButton";
import {
  generateIdeas, getGigs, getProducts, getTasks, getCrypto,
  getFeed, getSaved, saveOpportunity, delSaved, getEarnings, logoutApi,
  refreshProducts, getAlertPrefs, setAlertPrefs, sendTestAlert, getAlertsLog,
  sendDigestNow, generatePlan, getPlan, verifyStart,
  addEarningLog, listEarningLogs, deleteEarningLog, createFeeCheckout, getPaymentStatus,
  paypalStatus, paypalCreateOrder, paypalCapture,
  exportUserData, deleteUserAccount,
  getMyReferral, connectOnboard, connectStatus,
  getUserRole,
} from "@/lib/api";
import { LineChart, Line, ResponsiveContainer } from "recharts";

// SEC: only permit http(s) URLs from any data source — guards against javascript:/data:/etc.
const safeUrl = (u) => {
  if (typeof u !== "string") return "#";
  const trimmed = u.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return "#";
};

const Surface = ({ children, className = "", testId, glow = false }) => (
  <div
    data-testid={testId}
    className={`bg-zinc-950/80 border ${glow ? "border-[#60a5fa]/30 glow-border-green" : "border-zinc-800/60"} p-6 relative ${className}`}
  >
    {children}
  </div>
);

const Tag = ({ children, c = "#e2e8f0" }) => (
  <span className="text-[10px] tracking-[0.25em] uppercase border px-2 py-0.5 font-mono" style={{ color: c, borderColor: c + "55" }}>{children}</span>
);

function Header({ user, onLogout, isAdmin }) {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 border-b border-zinc-800/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-9 w-20 shrink-0 bg-zinc-900 border border-zinc-800 rounded-md flex items-center justify-center p-1 overflow-hidden">
            <img
              data-testid="dash-logo-nc"
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-cash-mvp-2/artifacts/0086jwtl_NC%20Const.Logo%20Editable%20Jul%2017%2C%202026%2C%2012_37_50%20AM.png"
              alt="NC Construction LLC"
              className="max-h-full max-w-full object-contain"
            />
          </div>
          <div className="h-9 w-20 shrink-0 bg-zinc-900 border border-zinc-800 rounded-md flex items-center justify-center p-1 overflow-hidden">
            <img
              data-testid="dash-logo-clark"
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-cash-mvp-2/artifacts/c2mpfljt_image.png"
              alt="Clark Properties"
              className="max-h-full max-w-full object-contain"
            />
          </div>
          <div className="h-9 w-20 shrink-0 bg-zinc-900 border border-zinc-800 rounded-md flex items-center justify-center p-1 overflow-hidden">
            <img
              data-testid="dash-logo-vip"
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-cash-mvp-2/artifacts/7fk0o84l_MoneyHub%20LogoBest%20Image%20Jul%2017%2C%202026%2C%2012_33_13%20AM.png"
              alt="MoneyHub VIP"
              className="max-h-full max-w-full object-contain"
            />
          </div>
          <div className="hidden sm:flex flex-col leading-tight ml-1">
            <span className="font-heading font-black tracking-tight text-xs">NC CONSTRUCTION × CLARK PROPERTIES × MONEYHUB VIP</span>
            <span className="text-[9px] tracking-[0.3em] uppercase text-[#60a5fa] mt-0.5">Money/Hub · control room</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="user-name" className="text-xs text-zinc-200 hidden sm:inline">{user?.name}</span>
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-7 w-7 border border-zinc-700" />
          ) : (
            <div className="h-7 w-7 bg-zinc-800 border border-zinc-700" />
          )}
          <a
            data-testid="settings-nav-button"
            href="/settings"
            aria-label="Settings"
            title="Settings"
            className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 hover:text-[#60a5fa] transition-colors px-3 py-1 border border-zinc-800 hover:border-[#60a5fa]/50 inline-flex items-center gap-1.5"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            Settings
          </a>
          {isAdmin && (
            <a
              data-testid="admin-nav-button"
              href="/vault-nc-9k7x2q-panel"
              className="text-[10px] tracking-[0.3em] uppercase text-[#22c55e] border border-[#22c55e]/50 hover:bg-[#22c55e]/10 transition-colors px-3 py-1"
            >
              Admin
            </a>
          )}
          <button
            data-testid="logout-button"
            onClick={onLogout}
            className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 hover:text-[#fb7185] transition-colors px-3 py-1 border border-zinc-800 hover:border-[#fb7185]/50"
          >
            Logout
          </button>
        </div>
      </div>
    </header>
  );
}

function EarningsWidget({ earnings, cashtag, depositLinks }) {
  // In test launch, EarningsWidget becomes a lightweight "Saved opportunities" summary.
  if (TEST_LAUNCH_MODE) {
    return (
      <Surface testId="earnings-widget" glow className="lg:col-span-1">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-200">Saved opportunities</div>
        <div data-testid="earnings-metric-value" className="font-heading text-4xl md:text-5xl font-black neon-green mt-3">
          {earnings?.saved_count ?? 0}
        </div>
        <div className="mt-4 text-xs text-zinc-300 leading-relaxed">
          Opportunities you&apos;ve bookmarked from the 5 streams. Open <a href="#saved" className="text-[#60a5fa] hover:underline">Saved</a> to plan your next move.
        </div>
        <div className="mt-4 border border-zinc-800 p-3 text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-mono">
          // discovery + planning only · no payments in test launch
        </div>
      </Surface>
    );
  }
  const cashUrl = cashtag ? `https://cash.app/$${encodeURIComponent(cashtag)}` : null;
  const others = [
    { key: "paypal", href: depositLinks?.paypal, label: "PayPal",  color: "#00457c" },
    { key: "venmo",  href: depositLinks?.venmo,  label: "Venmo",   color: "#3d95ce" },
    { key: "zelle",  href: depositLinks?.zelle,  label: "Zelle",   color: "#6d1ed4" },
  ].filter((x) => !!x.href);
  return (
    <Surface testId="earnings-widget" glow className="lg:col-span-1">
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-200">Potential weekly</div>
      <div data-testid="earnings-metric-value" className="font-heading text-4xl md:text-5xl font-black neon-green mt-3">
        ${earnings?.potential_weekly ?? 0}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div className="border border-zinc-800 p-2">
          <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-300">Monthly</div>
          <div className="font-mono text-white mt-1">${earnings?.potential_monthly ?? 0}</div>
        </div>
        <div className="border border-zinc-800 p-2">
          <div className="text-[9px] tracking-[0.2em] uppercase text-zinc-300">Saved</div>
          <div className="font-mono text-white mt-1">{earnings?.saved_count ?? 0}</div>
        </div>
      </div>

      {cashUrl ? (
        <a
          data-testid="cashout-button"
          href={cashUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 flex items-center justify-between gap-3 border border-[#22c55e]/60 bg-[#22c55e]/10 hover:bg-[#22c55e]/20 px-3 py-2.5 transition-all group"
        >
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#22c55e]">Deposit → Cash App</span>
          <span className="font-mono text-xs text-white">${cashtag} <span className="text-[#22c55e] group-hover:translate-x-0.5 inline-block transition-transform">→</span></span>
        </a>
      ) : (
        <div data-testid="cashout-empty" className="mt-4 border border-dashed border-zinc-800 px-3 py-2.5 text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-mono">
          // add Cashtag in alerts settings to enable 1-tap deposit
        </div>
      )}

      <div className="mt-3 text-[10px] text-zinc-300 leading-relaxed">
        Estimated from saved opportunities × avg $75/wk per item.
      </div>

      {others.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {others.map((o) => (
            <a
              key={o.key}
              href={o.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`deposit-${o.key}`}
              style={{ borderColor: `${o.color}90`, color: o.color }}
              className="text-[9px] tracking-[0.25em] uppercase border px-2 py-1 hover:bg-white/5 transition-colors font-mono"
            >
              → {o.label}
            </a>
          ))}
        </div>
      )}
    </Surface>
  );
}

function IdeaGen({ onSaved }) {
  const [skills, setSkills] = useState("");
  const [time, setTime] = useState("weekend");
  const [capital, setCapital] = useState("0");
  const [brain, setBrain] = useState("claude");
  const [loading, setLoading] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [mode, setMode] = useState("");

  const run = async () => {
    setLoading(true);
    try {
      const r = await generateIdeas({ skills, time_available: time, starting_capital: capital, model: brain });
      setIdeas(r.ideas || []);
      setMode(r.model || r.mode);
      toast.success(`Generated ${r.ideas.length} ideas (${r.model || r.mode})`);
    } catch {
      toast.error("Failed to generate ideas");
    } finally { setLoading(false); }
  };

  const save = async (idea) => {
    try {
      await saveOpportunity({
        opportunity_id: idea.title.toLowerCase().replace(/\s+/g, "-").slice(0,40),
        opportunity_type: "idea",
        title: idea.title,
        payout: idea.estimated_payout || "",
        payload: idea,
      });
      toast.success("Saved");
      onSaved?.();
    } catch { toast.error("Save failed"); }
  };

  return (
    <Surface testId="idea-generator" className="lg:col-span-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#e2e8f0]">// AI Side-hustle generator</div>
          <h3 className="font-heading text-xl font-bold mt-2">Print money. Bring receipts.</h3>
        </div>
        {mode && <Tag c="#60a5fa">{mode}</Tag>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-5">
        <div className="flex gap-2 md:col-span-2">
          <input
            data-testid="ideagen-skills"
            value={skills} onChange={(e)=>setSkills(e.target.value)}
            placeholder="your skills (e.g. writing, design)"
            className="flex-1 min-w-0 bg-black border border-zinc-800 focus:border-[#e2e8f0] focus:outline-none focus:ring-1 focus:ring-[#e2e8f0] text-white placeholder-zinc-300 rounded-none font-mono text-xs px-3 py-3"
          />
          <MicButton
            testId="ideagen-mic"
            onTranscript={(t) => setSkills((prev) => (prev ? `${prev}, ${t}` : t))}
          />
        </div>
        <select
          data-testid="ideagen-time"
          value={time} onChange={(e)=>setTime(e.target.value)}
          className="bg-black border border-zinc-800 focus:border-[#e2e8f0] text-white rounded-none font-mono text-xs px-3 py-3 outline-none"
        >
          <option value="weekend">weekends only</option>
          <option value="evenings">evenings (2h/day)</option>
          <option value="fulltime">full-time</option>
        </select>
        <input
          data-testid="ideagen-capital"
          value={capital} onChange={(e)=>setCapital(e.target.value)}
          placeholder="capital USD (0 ok)"
          className="bg-black border border-zinc-800 focus:border-[#e2e8f0] focus:outline-none focus:ring-1 focus:ring-[#e2e8f0] text-white placeholder-zinc-300 rounded-none font-mono text-xs px-3 py-3"
        />
      </div>

      <div className="mt-3 flex items-center gap-2">
        <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500">brain</span>
        {[
          { key: "claude", label: "Claude" },
          { key: "chatgpt", label: "ChatGPT" },
          { key: "gemini", label: "Gemini" },
        ].map((m) => (
          <button
            key={m.key}
            type="button"
            data-testid={`ideagen-brain-${m.key}`}
            onClick={() => setBrain(m.key)}
            className={`text-[9px] tracking-[0.25em] uppercase border px-2.5 py-1 font-mono transition-colors ${
              brain === m.key ? "border-[#60a5fa] text-[#60a5fa] bg-[#60a5fa]/10" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <button
        data-testid="generate-ideas-button"
        onClick={run} disabled={loading}
        className="mt-4 bg-[#60a5fa] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-[#2563eb] transition-all hover:drop-shadow-[0_0_15px_rgba(59,130,246,0.6)] rounded-none border border-[#60a5fa] active:scale-95 disabled:opacity-50"
      >
        {loading ? "Cooking…" : "▸ Generate"}
      </button>

      <div className="mt-6 space-y-3 max-h-[420px] overflow-y-auto scrollbar-thin pr-2">
        {ideas.length === 0 && (
          <div className="text-xs text-zinc-300 font-mono">// hit generate to see opportunities ranked by ROI</div>
        )}
        {ideas.map((it, i) => (
          <div key={`${it.title}-${i}`} data-testid={`idea-card-${i}`} className="border border-zinc-800 hover:border-[#60a5fa]/40 p-4 transition-colors">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-heading text-base font-bold">{it.title}</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-200 mt-1">{it.speed} · {it.effort} effort</div>
              </div>
              <div className="font-mono text-sm neon-green whitespace-nowrap">{it.estimated_payout}</div>
            </div>
            {it.why_trending && <p className="mt-3 text-xs text-zinc-200 leading-relaxed">{it.why_trending}</p>}
            {it.steps && (
              <ol className="mt-3 space-y-1 text-xs text-zinc-200">
                {it.steps.map((s, j) => <li key={`${it.title}-step-${j}`} className="font-mono">› {s}</li>)}
              </ol>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                data-testid={`save-idea-${i}`}
                onClick={() => save(it)}
                className="text-[10px] tracking-[0.3em] uppercase text-[#e2e8f0] hover:text-white border border-[#e2e8f0]/40 hover:border-[#e2e8f0] px-3 py-1.5 transition-colors"
              >
                + Save
              </button>
              <ListenButton
                testId={`listen-idea-${i}`}
                text={`${it.title}. Payout: ${it.estimated_payout || "varies"}. ${it.why_trending || ""} Steps: ${(it.steps || []).join(". ")}`}
              />
              <MockupButton
                testId={`mockup-idea-${i}`}
                prompt={it.title}
                defaultStyle="thumbnail"
              />
            </div>
          </div>
        ))}
      </div>
    </Surface>
  );
}

function CryptoWidget() {
  const [coins, setCoins] = useState([]);
  useEffect(() => { getCrypto().then(r => setCoins(r.coins || [])).catch((err) => devWarn("Crypto fetch failed:", err?.message || err)); }, []);
  return (
    <Surface testId="crypto-widget">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#fb7185]">// crypto movers</div>
        <Tag c="#fb7185">LIVE</Tag>
      </div>
      <div className="mt-4 divide-y divide-zinc-800">
        {coins.length === 0 && <div className="text-xs text-zinc-300 font-mono py-4">loading market…</div>}
        {coins.map((c) => {
          const up = (c.change_24h || 0) >= 0;
          const data = (c.sparkline || []).map((v, i) => ({ i, v }));
          return (
            <div key={c.id} data-testid={`coin-${c.symbol}`} className="py-3 flex items-center gap-3">
              <img src={c.image} alt="" className="h-6 w-6" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between">
                  <div className="font-heading text-sm font-bold truncate">{c.symbol}</div>
                  <div className="font-mono text-xs text-white">${(c.price ?? 0).toLocaleString(undefined,{maximumFractionDigits: c.price < 1 ? 6 : 2})}</div>
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <div className="text-[10px] text-zinc-300 truncate">{c.name}</div>
                  <div className={`font-mono text-xs ${up ? "neon-green" : "neon-red"}`}>{up ? "+" : ""}{(c.change_24h || 0).toFixed(2)}%</div>
                </div>
              </div>
              <div className="h-8 w-16 shrink-0">
                <ResponsiveContainer>
                  <LineChart data={data}>
                    <Line type="monotone" dataKey="v" stroke={up ? "#60a5fa" : "#fb7185"} strokeWidth={1.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })}
      </div>
    </Surface>
  );
}

function ListWidget({ title, accent, items, render, testId }) {
  return (
    <Surface testId={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: accent }}>{title}</div>
        <Tag c={accent}>{items.length}</Tag>
      </div>
      <div className="mt-4 space-y-3 max-h-[420px] overflow-y-auto scrollbar-thin pr-2">
        {items.map((it, i) => render(it, i))}
      </div>
    </Surface>
  );
}

function FeedWidget({ feed, onSave }) {
  return (
    <Surface testId="feed-widget">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa]">// money hub feed</div>
          <h3 className="font-heading text-lg font-bold mt-2">Ranked by speed-to-cash</h3>
        </div>
        <Tag c="#60a5fa">{feed.length}</Tag>
      </div>
      <div className="mt-4 space-y-2 max-h-[520px] overflow-y-auto scrollbar-thin pr-2">
        {feed.map((it, i) => (
          <div key={`${it.type}-${it.id}`} data-testid={`feed-item-${i}`} className="border border-zinc-800 hover:border-[#60a5fa]/40 p-3 flex items-center gap-3 group transition-colors">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-300 w-16 shrink-0">{it.type}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-white truncate">{it.title}</div>
              <div className="text-[10px] text-zinc-200 truncate">{it.tag}</div>
            </div>
            <div className="font-mono text-xs neon-green whitespace-nowrap">{it.payout}</div>
            <button
              data-testid={`feed-save-${i}`}
              onClick={() => onSave(it)}
              className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 hover:text-[#e2e8f0] border border-zinc-800 hover:border-[#e2e8f0]/40 px-2 py-1 transition-colors"
            >+</button>
          </div>
        ))}
      </div>
    </Surface>
  );
}

function PlanPanel({ plan }) {
  if (!plan) return null;
  return (
    <div className="mt-3 border-t border-zinc-800 pt-3 space-y-3" data-testid="plan-panel">
      {plan.summary && (
        <div className="text-xs text-zinc-200 leading-relaxed">{plan.summary}</div>
      )}
      {plan.estimated_first_dollar && (
        <div className="text-[10px] tracking-[0.25em] uppercase">
          <span className="text-zinc-200">First $ target:</span>{" "}
          <span className="neon-green">{plan.estimated_first_dollar}</span>
        </div>
      )}
      <div className="space-y-1.5">
        {(plan.days || []).map((d, i) => (
          <details key={`${d.day}-${i}`} className="border border-zinc-800 hover:border-[#60a5fa]/30">
            <summary className="cursor-pointer px-2.5 py-1.5 flex items-center justify-between text-xs">
              <span className="font-mono text-zinc-200">{d.day}</span>
              <span className="text-white font-bold truncate ml-2 flex-1">{d.title}</span>
            </summary>
            <div className="px-2.5 pb-2.5 pt-1 space-y-1.5">
              <ul className="space-y-1 text-[11px] text-zinc-200">
                {(d.tasks || []).map((t, j) => <li key={`${(t||'').slice(0,30)}-${j}`} className="font-mono leading-relaxed">› {t}</li>)}
              </ul>
              {d.deliverable && (
                <div className="text-[10px] tracking-[0.2em] uppercase">
                  <span className="text-zinc-300">Deliverable:</span>{" "}
                  <span className="neon-green">{d.deliverable}</span>
                </div>
              )}
            </div>
          </details>
        ))}
      </div>
      {plan.tools?.length > 0 && (
        <div>
          <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-300 mb-1">Stack</div>
          <div className="flex flex-wrap gap-1">
            {plan.tools.map((t, i) => (
              <span key={`${t}-${i}`} className="text-[10px] font-mono text-[#e2e8f0] border border-zinc-800 px-1.5 py-0.5">{t}</span>
            ))}
          </div>
        </div>
      )}
      {plan.warnings?.length > 0 && (
        <div>
          <div className="text-[9px] tracking-[0.25em] uppercase text-[#fb7185] mb-1">Watch out</div>
          <ul className="space-y-0.5 text-[10px] text-zinc-200">
            {plan.warnings.map((w, i) => <li key={`${(w||'').slice(0,30)}-${i}`} className="font-mono">! {w}</li>)}
          </ul>
        </div>
      )}
      <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-400">
        mode: {plan.mode || "ai"}
      </div>
    </div>
  );
}

function SavedItem({ s, onDel, onUpdate }) {
  const [plan, setPlan] = useState(s.action_plan || null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const togglePlan = async () => {
    if (open) { setOpen(false); return; }
    setLoading(true);
    try {
      if (plan) {
        setOpen(true);
      } else {
        const cached = await getPlan(s.id);
        if (cached.plan) {
          setPlan(cached.plan);
        } else {
          const r = await generatePlan(s.id);
          setPlan(r.plan);
          toast.success(`7-day plan generated (${r.plan.mode})`);
          onUpdate?.();
        }
        setOpen(true);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to generate plan";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const regen = async () => {
    setLoading(true);
    try {
      const r = await generatePlan(s.id);
      setPlan(r.plan);
      toast.success(`Plan regenerated (${r.plan.mode})`);
      onUpdate?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Regen failed";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid={`saved-${s.id}`} className="border border-zinc-800 p-3">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-white truncate">{s.title}</div>
          <div className="text-[10px] text-zinc-200">{s.opportunity_type} · {s.payout}</div>
        </div>
        <button
          data-testid={`plan-toggle-${s.id}`}
          onClick={togglePlan}
          disabled={loading}
          className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] border border-[#60a5fa]/40 hover:border-[#60a5fa] hover:bg-[#60a5fa]/10 px-2 py-1 transition-colors disabled:opacity-50"
        >
          {(() => {
            if (loading) return "…";
            if (open) return "Hide";
            return plan ? "Plan" : "▸ Plan";
          })()}
        </button>
        <button
          data-testid={`del-saved-${s.id}`}
          onClick={() => onDel(s.id)}
          className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 hover:text-[#fb7185] transition-colors"
        >×</button>
      </div>
      {open && (
        <>
          <PlanPanel plan={plan} />
          <button
            data-testid={`plan-regen-${s.id}`}
            onClick={regen}
            disabled={loading}
            className="mt-2 text-[10px] tracking-[0.3em] uppercase text-zinc-200 hover:text-[#e2e8f0] transition-colors disabled:opacity-50"
          >↻ regen plan</button>
        </>
      )}
    </div>
  );
}

function SavedWidget({ saved, onDel, onUpdate }) {
  return (
    <Surface testId="saved-widget">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#e2e8f0]">// saved</div>
        <Tag c="#e2e8f0">{saved.length}</Tag>
      </div>
      <div className="mt-4 space-y-2 max-h-[420px] overflow-y-auto scrollbar-thin pr-2">
        {saved.length === 0 && <div className="text-xs text-zinc-300 font-mono">// nothing saved yet — save anything to unlock 7-day AI plans</div>}
        {saved.map((s) => <SavedItem key={s.id} s={s} onDel={onDel} onUpdate={onUpdate} />)}
      </div>
    </Surface>
  );
}

function ReferralWidget() {
  const [ref, setRef] = useState(null);
  const [connect, setConnect] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r, c] = await Promise.all([getMyReferral(), connectStatus()]);
        setRef(r);
        setConnect(c);
      } catch (err) {
        devWarn("referral load failed:", err?.message || err);
      }
    })();
  }, []);

  const copy = async () => {
    if (!ref?.code) return;
    const shareUrl = `${window.location.origin}${ref.share_url_template}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Referral link copied — share it to earn credits");
    } catch {
      toast.message(shareUrl);
    }
  };

  const startConnect = async () => {
    setBusy(true);
    try {
      const r = await connectOnboard();
      window.location.href = r.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Stripe Connect not ready — enable it on your Stripe dashboard first");
      setBusy(false);
    }
  };

  return (
    <Surface testId="referral-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#00F0FF]">// referrals · payouts</div>
        <div className="text-[10px] font-mono text-zinc-200">
          {ref ? `${ref.invited_count} invited · ${ref.credits_remaining} credits` : "…"}
        </div>
      </div>
      <div className="border border-zinc-800 p-3 mb-3">
        <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-300">Your referral code</div>
        <div className="mt-1 flex items-center gap-2">
          <code data-testid="referral-code" className="font-mono text-lg text-[#00F0FF] tracking-wider">{ref?.code || "…"}</code>
          <button
            data-testid="referral-copy"
            onClick={copy}
            className="ml-auto text-[10px] tracking-[0.3em] uppercase text-[#00F0FF] border border-[#00F0FF]/40 hover:bg-[#00F0FF]/10 px-3 py-1.5 transition-colors"
          >Copy link</button>
        </div>
        <div className="mt-2 text-[10px] font-mono text-zinc-300 leading-relaxed">
          // Both of you get 5 credits (5% off next 5 platform fees) when a friend joins & claims your code.
        </div>
      </div>

      {!TEST_LAUNCH_MODE && (
        <div className="border border-zinc-800 p-3">
          <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-300">Direct payouts (Stripe Connect)</div>
          {connect?.payouts_enabled ? (
            <div className="mt-1 text-xs font-mono text-[#22c55e]">✓ Connected · payouts active</div>
          ) : connect?.connected ? (
            <>
              <div className="mt-1 text-xs font-mono text-[#ffc439]">⏳ Onboarding incomplete</div>
              <button
                data-testid="stripe-connect-resume"
                onClick={startConnect}
                disabled={busy}
                className="mt-2 text-[10px] tracking-[0.3em] uppercase text-black bg-[#ffc439] hover:bg-[#ffb800] px-3 py-2 font-bold disabled:opacity-50"
              >{busy ? "…" : "Finish onboarding →"}</button>
            </>
          ) : (
            <>
              <div className="mt-1 text-xs font-mono text-zinc-200">Link your bank via Stripe to receive direct payouts.</div>
              <button
                data-testid="stripe-connect-start"
                onClick={startConnect}
                disabled={busy}
                className="mt-2 text-[10px] tracking-[0.3em] uppercase text-black bg-[#60a5fa] hover:bg-[#60a5fa] px-3 py-2 font-bold disabled:opacity-50"
              >{busy ? "…" : "Connect payouts →"}</button>
            </>
          )}
        </div>
      )}
    </Surface>
  );
}

function LedgerWidget({ onEarningsChange }) {
  const [logs, setLogs] = useState([]);
  const [source, setSource] = useState("");
  const [amount, setAmount] = useState("");
  const [stream, setStream] = useState("gig");
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payingPayPal, setPayingPayPal] = useState(false);
  const [ppEnabled, setPpEnabled] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await listEarningLogs();
      setLogs(r.items || []);
    } catch (err) {
      devWarn("ledger load failed:", err?.message || err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const s = await paypalStatus();
        setPpEnabled(!!s?.enabled);
      } catch (_) { setPpEnabled(false); }
    })();
  }, []);

  const unpaid = logs.filter((l) => !l.fee_paid);
  const feeOwed = unpaid.reduce((sum, l) => sum + Number(l.platform_fee || 0), 0);
  const gross = logs.reduce((sum, l) => sum + Number(l.amount || 0), 0);
  const feesPaid = logs.filter((l) => l.fee_paid).reduce((sum, l) => sum + Number(l.platform_fee || 0), 0);

  const add = async () => {
    const amt = parseFloat(amount);
    if (!source.trim()) { toast.error("Enter a source"); return; }
    if (!(amt > 0)) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await addEarningLog({ source: source.trim(), amount: amt, stream });
      setSource(""); setAmount("");
      toast.success(`Logged $${amt.toFixed(2)} · $${(amt * 0.20).toFixed(2)} platform fee accrued`);
      await load();
      onEarningsChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Log failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await deleteEarningLog(id);
      await load();
      onEarningsChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const payFee = async () => {
    if (feeOwed < 0.50) { toast.error("Fee must be at least $0.50 (Stripe minimum)"); return; }
    setPaying(true);
    try {
      const { url } = await createFeeCheckout(window.location.origin);
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setPaying(false);
    }
  };

  const payFeePayPal = async () => {
    if (feeOwed < 0.50) { toast.error("Fee must be at least $0.50"); return; }
    setPayingPayPal(true);
    try {
      const { url } = await paypalCreateOrder(window.location.origin);
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PayPal order failed");
      setPayingPayPal(false);
    }
  };

  return (
    <Surface testId="ledger-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#a78bfa]">// earnings ledger · 20% platform fee</div>
        <div className="text-[10px] font-mono text-zinc-200">{logs.length} entries</div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="border border-zinc-800 p-3">
          <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-300">Gross</div>
          <div className="font-mono text-lg text-white mt-1">${gross.toFixed(2)}</div>
        </div>
        <div className="border border-[#a78bfa]/40 bg-[#a78bfa]/5 p-3">
          <div className="text-[9px] tracking-[0.25em] uppercase text-[#a78bfa]">Fee owed</div>
          <div className="font-mono text-lg text-[#a78bfa] mt-1">${feeOwed.toFixed(2)}</div>
        </div>
        <div className="border border-zinc-800 p-3">
          <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-300">Fees paid</div>
          <div className="font-mono text-lg text-[#22c55e] mt-1">${feesPaid.toFixed(2)}</div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input
          data-testid="ledger-source-input"
          type="text"
          placeholder="Source (e.g. Fiverr headshots gig)"
          value={source}
          onChange={(e) => setSource(e.target.value.slice(0, 120))}
          className="flex-1 bg-black border border-zinc-800 focus:border-[#a78bfa] focus:outline-none text-white placeholder-zinc-300 rounded-none font-mono text-xs px-3 py-2.5"
        />
        <input
          data-testid="ledger-amount-input"
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder="$0.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full sm:w-28 bg-black border border-zinc-800 focus:border-[#a78bfa] focus:outline-none text-white placeholder-zinc-300 rounded-none font-mono text-xs px-3 py-2.5"
        />
        <select
          data-testid="ledger-stream-select"
          value={stream}
          onChange={(e) => setStream(e.target.value)}
          className="bg-black border border-zinc-800 focus:border-[#a78bfa] focus:outline-none text-white rounded-none font-mono text-xs px-3 py-2.5"
        >
          <option value="gig">gig</option>
          <option value="product">product</option>
          <option value="crypto">crypto</option>
          <option value="task">task</option>
          <option value="other">other</option>
        </select>
        <button
          data-testid="ledger-add-button"
          onClick={add}
          disabled={busy}
          className="text-[10px] tracking-[0.3em] uppercase text-black bg-[#a78bfa] hover:bg-[#c4b5fd] px-4 py-2.5 transition-colors disabled:opacity-50 whitespace-nowrap font-bold"
        >
          {busy ? "…" : "Log +"}
        </button>
      </div>

      <button
        data-testid="pay-fee-button"
        onClick={payFee}
        disabled={paying || feeOwed < 0.50}
        className="w-full flex items-center justify-between border border-[#a78bfa]/60 bg-[#a78bfa]/10 hover:bg-[#a78bfa]/20 px-4 py-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed group"
      >
        <span className="text-[11px] tracking-[0.3em] uppercase text-[#a78bfa]">
          {paying ? "Redirecting…" : feeOwed < 0.50 ? "No fee owed" : "Pay with Stripe →"}
        </span>
        <span className="font-mono text-sm text-white">${feeOwed.toFixed(2)}</span>
      </button>

      {ppEnabled && (
        <button
          data-testid="pay-fee-paypal-button"
          onClick={payFeePayPal}
          disabled={payingPayPal || feeOwed < 0.50}
          className="mt-2 w-full flex items-center justify-between border border-[#ffc439] bg-[#ffc439] hover:bg-[#ffb800] text-black px-4 py-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <span className="text-[11px] tracking-[0.3em] uppercase font-bold">
            {payingPayPal ? "Redirecting…" : "Pay with PayPal →"}
          </span>
          <span className="font-mono text-sm">${feeOwed.toFixed(2)}</span>
        </button>
      )}

      <div className="mt-4 max-h-56 overflow-y-auto scrollbar-thin pr-1">
        {logs.length === 0 && (
          <div className="text-xs text-zinc-300 font-mono py-3">// log any real payout you earned via Money/Hub. 20% is your fee to the platform.</div>
        )}
        {logs.map((l) => (
          <div key={l.id} className="flex items-center justify-between gap-2 border-b border-zinc-900 py-2 text-xs">
            <div className="min-w-0 flex-1">
              <div className="font-mono text-white truncate">{l.source}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300 mt-0.5">
                {l.stream} · ${Number(l.amount).toFixed(2)} gross · {new Date(l.created_at).toLocaleDateString()}
                {l.fee_paid && <span className="text-[#22c55e] ml-2">✓ fee paid</span>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono text-[#a78bfa]">-${Number(l.platform_fee).toFixed(2)}</div>
              {!l.fee_paid && (
                <button
                  onClick={() => remove(l.id)}
                  className="text-[9px] tracking-[0.25em] uppercase text-zinc-400 hover:text-[#fb7185]"
                >remove</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Surface>
  );
}

function AlertsWidget({ onSaved, onLoaded }) {
  const [prefs, setPrefs] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, l] = await Promise.all([getAlertPrefs(), getAlertsLog()]);
      setPrefs(p);
      setLog(l.items || []);
      onLoaded?.(p);
    } catch (err) {
      devWarn("Alerts load failed:", err?.message || err);
    }
  }, [onLoaded]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const saved = await setAlertPrefs(prefs);
      setPrefs(saved);
      toast.success("Alert prefs saved");
      onSaved?.(saved);
    }
    catch (err) {
      const d = err?.response?.data?.detail;
      let msg = "Save failed";
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d) && d.length > 0) {
        const first = d[0];
        const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : null;
        const raw = String(first?.msg || "").replace(/^Value error,\s*/i, "");
        msg = raw ? (field ? `${field}: ${raw}` : raw) : msg;
      }
      toast.error(msg);
    }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = await sendTestAlert();
      if (r.ok) toast.success(`Test sent to ${r.sent_to}`);
      else toast.error(`Send failed: ${r.error || "unknown"}`);
      load();
    } catch { toast.error("Send failed"); }
    finally { setBusy(false); }
  };

  if (!prefs) return null;

  return (
    <Surface testId="alerts-widget" className="lg:col-span-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa]">// email alerts</div>
          <h3 className="font-heading text-lg font-bold mt-2">Get pinged when money shows up</h3>
        </div>
        <div className="flex gap-2">
          <button data-testid="digest-now-button" onClick={async () => {
            setBusy(true);
            try { const r = await sendDigestNow(); if (r.ok) toast.success(`Digest sent to ${r.sent_to}`); else toast.error(`Digest failed: ${r.error||""}`); load(); }
            catch { toast.error("Digest failed"); } finally { setBusy(false); }
          }} disabled={busy} className="text-[10px] tracking-[0.3em] uppercase text-[#e2e8f0] border border-zinc-800 hover:border-[#e2e8f0] px-3 py-1.5 transition-colors disabled:opacity-50">↳ Send digest</button>
          <button data-testid="alerts-test-button" onClick={test} disabled={busy} className="text-[10px] tracking-[0.3em] uppercase text-[#e2e8f0] border border-zinc-800 hover:border-[#e2e8f0] px-3 py-1.5 transition-colors disabled:opacity-50">Send test</button>
          <button data-testid="alerts-save-button" onClick={save} disabled={busy} className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] border border-[#60a5fa]/50 hover:border-[#60a5fa] hover:bg-[#60a5fa]/10 px-3 py-1.5 transition-colors disabled:opacity-50">Save</button>
        </div>
      </div>

      <div className="mt-4">
        <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-200 block mb-1">Google account email · always safe</label>
        <input
          data-testid="alerts-email-input"
          value={prefs.email || ""}
          readOnly
          disabled
          className="w-full bg-zinc-900 border border-zinc-800 text-zinc-200 rounded-none font-mono text-xs px-3 py-2.5 cursor-not-allowed"
        />
      </div>

      <div className="mt-3">
        <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-200 block mb-1">
          Alternate delivery address {prefs.alt_email_verified ? <span className="text-[#60a5fa]">· verified ✓</span> : (prefs.alt_email ? <span className="text-[#fb7185]">· unverified</span> : "")}
        </label>
        <div className="flex gap-2">
          <input
            data-testid="alerts-alt-email-input"
            type="email"
            placeholder="you@yourdomain.com"
            value={prefs.alt_email || ""}
            onChange={(e) => {
              const v = e.target.value;
              // Editing the alt_email invalidates any prior verification (both server + client enforce this).
              setPrefs({ ...prefs, alt_email: v, alt_email_verified: false });
            }}
            className="flex-1 bg-black border border-zinc-800 focus:border-[#60a5fa] focus:outline-none text-white placeholder-zinc-300 rounded-none font-mono text-xs px-3 py-2.5"
          />
          <button
            data-testid="verify-alt-button"
            onClick={async () => {
              if (!prefs.alt_email || !prefs.alt_email.includes("@")) { toast.error("Enter a valid email first"); return; }
              setBusy(true);
              try {
                const r = await verifyStart(prefs.alt_email);
                if (r.email_sent) {
                  toast.success("Verification email sent", {
                    duration: 12000,
                    description: `Check ${r.alt_email} for a confirmation link. Expires in 24 hours.`,
                  });
                } else {
                  toast.error("Delivery failed", {
                    duration: 12000,
                    description: `Money/Hub couldn't deliver to ${r.alt_email}. If you own moneyhubvip@outlook.com, verify Resend DNS or provide an Outlook SMTP app password so alerts can route there.`,
                  });
                }
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Verification failed");
              } finally { setBusy(false); }
            }}
            disabled={busy}
            className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] border border-[#60a5fa]/40 hover:border-[#60a5fa] hover:bg-[#60a5fa]/10 px-3 py-2.5 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {prefs.alt_email_verified && prefs.alt_email ? "Re-verify" : "Verify →"}
          </button>
        </div>
        <div className="mt-1.5 text-[10px] tracking-[0.2em] uppercase text-zinc-300 font-mono leading-relaxed">
          // Once verified, all alerts + daily digests route here instead of your Google email.
        </div>
      </div>

      <div className="mt-4">
        <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-200 block mb-1">
          Cash App Cashtag {prefs.cashtag ? <span className="text-[#22c55e]">· active ✓</span> : <span className="text-zinc-300">· optional</span>}
        </label>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[#22c55e] font-bold text-sm select-none">$</span>
          <input
            data-testid="alerts-cashtag-input"
            type="text"
            inputMode="text"
            autoCapitalize="none"
            spellCheck="false"
            maxLength={20}
            placeholder="yourcashtag"
            value={prefs.cashtag || ""}
            onChange={(e) => {
              // Allow any character (per user request). Only strip an optional leading $ and cap length.
              const raw = e.target.value.replace(/^\$/, "").slice(0, 40);
              setPrefs({ ...prefs, cashtag: raw });
            }}
            className="flex-1 bg-black border border-zinc-800 focus:border-[#22c55e] focus:outline-none text-white placeholder-zinc-300 rounded-none font-mono text-xs px-3 py-2.5"
          />
          {prefs.cashtag && (
            <a
              data-testid="cashtag-preview-link"
              href={`https://cash.app/$${encodeURIComponent(prefs.cashtag)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] tracking-[0.3em] uppercase text-[#22c55e] border border-[#22c55e]/50 hover:border-[#22c55e] hover:bg-[#22c55e]/10 px-3 py-2.5 transition-colors whitespace-nowrap"
            >
              Test →
            </a>
          )}
        </div>
        <div className="mt-1.5 text-[10px] tracking-[0.2em] uppercase text-zinc-300 font-mono leading-relaxed">
          // Enables 1-tap Deposit → Cash App links across every stream. Letters/numbers, up to 20 chars.
        </div>
      </div>

      {/* Universal cashout handles: PayPal.me, Venmo, Zelle */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
        {[
          { key: "paypal_me", label: "PayPal.me", prefix: "paypal.me/", color: "#00457c" },
          { key: "venmo",     label: "Venmo",     prefix: "@",           color: "#3d95ce" },
          { key: "zelle",     label: "Zelle",     prefix: "",            color: "#6d1ed4" },
        ].map(({ key, label, prefix, color }) => (
          <div key={key}>
            <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-200 block mb-1">
              {label} {prefs[key] ? <span style={{color}}>· ✓</span> : <span className="text-zinc-300">· optional</span>}
            </label>
            <div className="flex items-center gap-1">
              {prefix && <span className="font-mono text-zinc-200 text-xs select-none whitespace-nowrap">{prefix}</span>}
              <input
                data-testid={`alerts-${key}-input`}
                type="text"
                autoCapitalize="none"
                spellCheck="false"
                maxLength={key === "zelle" ? 60 : 30}
                placeholder={key === "zelle" ? "email or phone" : "handle"}
                value={prefs[key] || ""}
                onChange={(e) => setPrefs({ ...prefs, [key]: e.target.value })}
                className="flex-1 bg-black border border-zinc-800 focus:outline-none text-white placeholder-zinc-400 rounded-none font-mono text-xs px-2 py-2 focus:border-[#60a5fa]"
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-1 text-[10px] tracking-[0.2em] uppercase text-zinc-300 font-mono">
        // any account you set here becomes a 1-tap deposit button on your Earnings widget.
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
        {[
          {k: "product_score_min",   label: "Product score ≥", key: "enable_product"},
          {k: "gig_payout_min",      label: "Gig payout $ ≥",  key: "enable_gig"},
          {k: "crypto_pct_min",      label: "Crypto ±% ≥",     key: "enable_crypto"},
          {k: "task_hourly_min",     label: "Task $/hr ≥",     key: "enable_task"},
          {k: "milestone_step",      label: "Saved milestone every", key: "enable_milestone"},
          {k: "digest_hour_utc",     label: "Daily digest UTC hour", key: "enable_digest"},
        ].map(({k, label, key}) => (
          <div key={k} className="border border-zinc-800 p-3">
            <label className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-zinc-200 cursor-pointer">
              <input
                data-testid={`toggle-${key}`}
                type="checkbox"
                checked={!!prefs[key]}
                onChange={(e) => setPrefs({ ...prefs, [key]: e.target.checked })}
                className="accent-[#60a5fa]"
              />
              {label}
            </label>
            <input
              data-testid={`threshold-${k}`}
              type="number"
              value={prefs[k]}
              onChange={(e) => setPrefs({ ...prefs, [k]: Number(e.target.value) })}
              className="w-full mt-2 bg-black border border-zinc-800 focus:border-[#60a5fa] focus:outline-none text-white rounded-none font-mono text-sm px-2 py-1.5"
            />
          </div>
        ))}
      </div>

      <div className="mt-4 border-t border-zinc-800 pt-3">
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 mb-2">Recent alerts</div>
        <div className="space-y-1 max-h-32 overflow-y-auto scrollbar-thin pr-2">
          {log.length === 0 && <div className="text-[10px] font-mono text-zinc-300">// none yet</div>}
          {log.map((a) => (
            <div key={a.id} className="flex justify-between text-[10px] font-mono">
              <span className="text-zinc-200 truncate">{a.subject}</span>
              <span className={a.result?.ok ? "text-[#60a5fa]" : "text-[#fb7185]"}>{a.result?.ok ? "sent" : "fail"}</span>
            </div>
          ))}
        </div>
      </div>

      <PrivacyPanel />
    </Surface>
  );
}

function PrivacyPanel() {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const exportData = async () => {
    setBusy(true);
    try {
      const data = await exportUserData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `money-hub-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data exported");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (confirmText !== "DELETE") { toast.error("Type DELETE to confirm"); return; }
    setBusy(true);
    try {
      await deleteUserAccount();
      toast.success("Your account and data have been permanently deleted");
      setTimeout(() => { window.location.href = "/"; }, 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-zinc-800 pt-3">
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 mb-2">Privacy & data</div>
      <div className="flex flex-wrap gap-2">
        <a
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 hover:text-white border border-zinc-800 px-3 py-2 transition-colors"
        >Privacy policy →</a>
        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 hover:text-white border border-zinc-800 px-3 py-2 transition-colors"
        >Terms →</a>
        <button
          data-testid="export-user-data"
          onClick={exportData}
          disabled={busy}
          className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] hover:bg-[#60a5fa]/10 border border-[#60a5fa]/40 hover:border-[#60a5fa] px-3 py-2 transition-colors disabled:opacity-50"
        >⇩ Export my data</button>
        {!confirming ? (
          <button
            data-testid="delete-account-open"
            onClick={() => setConfirming(true)}
            className="text-[10px] tracking-[0.3em] uppercase text-[#fb7185] hover:bg-[#fb7185]/10 border border-[#fb7185]/40 hover:border-[#fb7185] px-3 py-2 transition-colors"
          >Delete my account</button>
        ) : (
          <div className="w-full flex flex-wrap items-center gap-2 mt-2 border border-[#fb7185]/40 bg-[#fb7185]/5 p-3">
            <div className="w-full text-[10px] font-mono text-[#fb7185] mb-1 leading-relaxed">
              // permanent · erases account, alerts, saved items, earnings log, payments · type <b>DELETE</b> below
            </div>
            <input
              data-testid="delete-account-confirm-input"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="flex-1 min-w-[120px] bg-black border border-[#fb7185]/40 focus:border-[#fb7185] focus:outline-none text-white placeholder-zinc-400 rounded-none font-mono text-xs px-3 py-2"
            />
            <button
              data-testid="delete-account-confirm-button"
              onClick={doDelete}
              disabled={busy || confirmText !== "DELETE"}
              className="text-[10px] tracking-[0.3em] uppercase text-black bg-[#fb7185] hover:bg-[#f43f5e] px-3 py-2 font-bold transition-colors disabled:opacity-40"
            >{busy ? "…" : "Confirm delete"}</button>
            <button
              onClick={() => { setConfirming(false); setConfirmText(""); }}
              className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 hover:text-white px-3 py-2"
            >Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [gigs, setGigs] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsMeta, setProductsMeta] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [feed, setFeed] = useState([]);
  const [saved, setSaved] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [cashtag, setCashtag] = useState("");
  const [depositLinks, setDepositLinks] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    getUserRole().then((r) => setIsAdmin(!!r?.is_admin)).catch(() => {});
  }, []);
  const applyPrefs = useCallback((p) => {
    setCashtag(p?.cashtag || "");
    setDepositLinks({
      paypal: p?.paypal_me ? `https://paypal.me/${encodeURIComponent(p.paypal_me)}` : null,
      venmo: p?.venmo ? `https://venmo.com/u/${encodeURIComponent(p.venmo)}` : null,
      zelle: p?.zelle ? `mailto:${encodeURIComponent(p.zelle)}?subject=Zelle%20deposit` : null,
    });
  }, []);

  const refreshSaved = useCallback(async () => {
    try {
      const [s, e] = await Promise.all([getSaved(), getEarnings()]);
      setSaved(s.items || []);
      setEarnings(e);
    } catch (err) {
      devWarn("refreshSaved failed:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [g, p, t, f] = await Promise.all([getGigs(), getProducts(), getTasks(), getFeed()]);
        setGigs(g.gigs || []);
        setProducts(p.products || []);
        setProductsMeta({ last_refreshed: p.last_refreshed, next_refresh: p.next_refresh, mode: p.mode });
        setTasks(t.tasks || []);
        setFeed(f.items || []);
        await refreshSaved();
      } catch (err) {
        devError("Dashboard load failed:", err);
        toast.error("Could not load dashboard");
      }
    })();
  }, [refreshSaved]);

  // Stripe post-checkout return: poll status, then clear the query param.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sess = params.get("fee_paid");
    const cancelled = params.get("fee_cancelled");
    const ppReturn = params.get("pp_return");
    const ppCancel = params.get("pp_cancel");
    const ppOrder = params.get("token"); // PayPal appends ?token=<orderID>

    if (cancelled || ppCancel) {
      toast.info("Fee payment cancelled");
      window.history.replaceState({}, "", "/dashboard");
      return;
    }

    if (ppReturn && ppOrder) {
      // Capture the PayPal order server-side
      (async () => {
        try {
          const r = await paypalCapture(ppOrder);
          if (r.payment_status === "paid") {
            toast.success("Platform fee paid via PayPal — thank you!");
            refreshSaved();
          } else {
            toast.message(`PayPal status: ${r.payment_status}`);
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || "PayPal capture failed");
        } finally {
          window.history.replaceState({}, "", "/dashboard");
        }
      })();
      return;
    }

    if (!sess) return;
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const s = await getPaymentStatus(sess);
        if (s.payment_status === "paid") {
          toast.success("Platform fee paid — thank you!");
          window.history.replaceState({}, "", "/dashboard");
          refreshSaved();
          return;
        }
        if (s.status === "expired") {
          toast.error("Checkout session expired");
          window.history.replaceState({}, "", "/dashboard");
          return;
        }
      } catch (err) {
        devWarn("payment status poll:", err?.message || err);
      }
      if (attempts < maxAttempts) setTimeout(poll, 2000);
      else toast.message("Still processing — refresh to check status");
    };
    poll();
  }, [refreshSaved]);

  const logout = async () => {
    try { await logoutApi(); } catch (err) { devWarn("Logout warning:", err?.message || err); }
    navigate("/", { replace: true });
  };

  const saveFromFeed = async (it) => {
    try {
      await saveOpportunity({
        opportunity_id: it.id,
        opportunity_type: it.type,
        title: it.title,
        payout: it.payout,
        payload: it,
      });
      toast.success("Saved");
      refreshSaved();
    } catch { toast.error("Save failed"); }
  };

  const saveCard = async (type, c) => {
    try {
      await saveOpportunity({
        opportunity_id: c.id,
        opportunity_type: type,
        title: c.title,
        payout: c.payout || c.avg_price || "",
        payload: c,
      });
      toast.success("Saved");
      refreshSaved();
    } catch { toast.error("Save failed"); }
  };

  const del = async (id) => {
    try { await delSaved(id); refreshSaved(); } catch { toast.error("Delete failed"); }
  };

  const runRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await refreshProducts();
      toast.success(`Catalog refreshed · mode: ${r.mode}`);
      const p = await getProducts();
      setProducts(p.products || []);
      setProductsMeta({ last_refreshed: p.last_refreshed, next_refresh: p.next_refresh, mode: p.mode });
    } catch { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };

  return (
    <div className="min-h-screen text-white">
      <Header user={user} onLogout={logout} isAdmin={isAdmin} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Push permission nudge — shown when the user hasn't opted into web push yet */}
        <PushPermissionNudge />
        {/* Discovery Mode banner — visible when TEST_LAUNCH_MODE is on */}
        {TEST_LAUNCH_MODE && (
          <div
            data-testid="discovery-mode-banner"
            className="mb-6 flex flex-wrap items-center justify-between gap-3 border border-[#a855f7]/50 bg-[#a855f7]/10 px-4 py-2.5"
          >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#a855f7] font-mono font-bold">Discovery mode</span>
              <span className="text-xs text-zinc-100">Payments unlock in:</span>
              <Countdown compact testId="dashboard-banner-countdown" />
            </div>
            <a
              data-testid="discovery-mode-support-link"
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-[10px] tracking-[0.3em] uppercase text-[#a855f7] hover:text-white transition-colors font-mono"
            >
              Questions? →
            </a>
          </div>
        )}

        {/* Referral stats — signed-in user's rank + count + share */}
        {TEST_LAUNCH_MODE && (
          <div className="mb-6 space-y-6">
            <ReferralStatsWidget />
            <ReferralChartWidget />
          </div>
        )}
        {/* Big wide MoneyHub VIP hero banner (member view) */}
        <div className="w-full mb-8 flex justify-center" data-testid="dashboard-hero-banner">
          <img
            src={require("../assets/moneyhub-vip-hero.png")}
            alt="MoneyHub VIP"
            className="w-full max-w-5xl h-auto object-contain drop-shadow-[0_0_45px_rgba(96,165,250,0.35)]"
          />
        </div>
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 font-mono">// control room</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mt-2">
              GO MAKE MONEY, <span className="neon-green">{(user?.name || "").split(" ")[0]?.toUpperCase()}</span>
            </h1>
          </div>
          <div className="hidden md:flex items-center gap-3">
            {cashtag ? (
              <a
                data-testid="control-room-cashout"
                href={`https://cash.app/$${encodeURIComponent(cashtag)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-[#22c55e]/60 bg-[#22c55e]/10 hover:bg-[#22c55e]/20 px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase text-[#22c55e] transition-colors"
              >
                <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm3.28 8.24c-.61-.5-1.4-.78-2.22-.78-.61 0-1.1.2-1.1.62 0 .43.53.63 1.44.9 1.7.5 2.7 1.25 2.7 2.65 0 1.36-1 2.34-2.55 2.55l-.13.75c-.05.32-.34.55-.67.55h-.34c-.35 0-.62-.32-.55-.66l.11-.63c-.86-.16-1.68-.55-2.24-1.06-.24-.22-.24-.6-.02-.83l.53-.52c.21-.2.55-.2.77-.02.55.44 1.28.7 2.05.7.79 0 1.32-.28 1.32-.75 0-.45-.44-.66-1.53-1.03-1.42-.47-2.55-1.13-2.55-2.53 0-1.44 1.16-2.28 2.52-2.44l.13-.7c.06-.32.35-.55.67-.55h.34c.35 0 .61.32.55.66l-.12.7c.61.16 1.15.44 1.6.79.24.19.27.55.06.77l-.5.52c-.21.22-.54.24-.78.06z"/></svg>
                Deposit → ${cashtag}
              </a>
            ) : null}
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono">
              {new Date().toLocaleString()}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <EarningsWidget earnings={earnings} cashtag={cashtag} depositLinks={depositLinks} />
          <IdeaGen onSaved={refreshSaved} />
          <CryptoWidget />

          {!TEST_LAUNCH_MODE && <LedgerWidget onEarningsChange={refreshSaved} />}
          <ReferralWidget />
          {!TEST_LAUNCH_MODE && (
            <div className="lg:col-span-2">
              <InvoicesWidget />
            </div>
          )}
          {!TEST_LAUNCH_MODE && <MarketplaceWidget />}

          <FeedWidget feed={feed} onSave={saveFromFeed} />

          <ListWidget
            testId="gigs-widget"
            title="// freelance gigs"
            accent="#e2e8f0"
            items={gigs}
            render={(g) => (
              <div key={g.id} data-testid={`gig-${g.id}`} className="border border-zinc-800 hover:border-[#e2e8f0]/40 p-3 transition-colors">
                <div className="flex items-baseline justify-between gap-2">
                  <a href={safeUrl(g.url)} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-white hover:text-[#e2e8f0] truncate">{g.title}</a>
                  <span className="font-mono text-xs neon-green whitespace-nowrap">{g.payout}</span>
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-200">{g.platform} · {g.speed}</div>
                  <button
                    data-testid={`save-gig-${g.id}`}
                    onClick={() => saveCard("gig", g)}
                    className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 hover:text-[#e2e8f0]"
                  >+ save</button>
                </div>
              </div>
            )}
          />

          <Surface testId="products-widget">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa]">// dropship products</div>
                <h3 className="font-heading text-lg font-bold mt-2">AI-curated · multi-channel</h3>
                <div className="text-[10px] text-zinc-200 font-mono mt-1">
                  {productsMeta.mode ? `mode: ${productsMeta.mode}` : ""}
                  {productsMeta.last_refreshed ? ` · refreshed ${new Date(productsMeta.last_refreshed).toLocaleString()}` : ""}
                </div>
              </div>
              <button
                data-testid="refresh-products-button"
                onClick={runRefresh}
                disabled={refreshing}
                className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] border border-[#60a5fa]/50 hover:border-[#60a5fa] hover:bg-[#60a5fa]/10 px-3 py-1.5 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {refreshing ? "Refreshing…" : "↻ Refresh AI"}
              </button>
            </div>
            <div className="mt-4 space-y-3 max-h-[520px] overflow-y-auto scrollbar-thin pr-2">
              {products.map((p) => (
                <div key={p.id} data-testid={`product-${p.id}`} className="border border-zinc-800 hover:border-[#60a5fa]/40 p-3 transition-colors">
                  <div className="flex items-baseline justify-between gap-2">
                    <a href={safeUrl(p.url)} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-white hover:text-[#60a5fa] truncate">{p.title}</a>
                    <span className="font-mono text-xs neon-green whitespace-nowrap">{p.margin}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-200">{p.source} · {p.avg_price} · score {p.trend_score}</div>
                    <button
                      data-testid={`save-product-${p.id}`}
                      onClick={() => saveCard("product", p)}
                      className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 hover:text-[#60a5fa]"
                    >+ save</button>
                  </div>
                  {p.channels && (
                    <div className="mt-2.5 pt-2.5 border-t border-zinc-800/70">
                      <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-300 mb-1.5">Launch on</div>
                      <div className="flex flex-wrap gap-1">
                        {p.channels.map((c) => (
                          <a
                            key={c.key}
                            data-testid={`channel-${p.id}-${c.key}`}
                            href={safeUrl(c.url)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[10px] font-mono tracking-wider text-zinc-200 hover:text-white border border-zinc-800 hover:border-[#60a5fa]/60 hover:bg-[#60a5fa]/10 px-1.5 py-0.5 transition-colors"
                          >{c.name}</a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Surface>

          <ListWidget
            testId="tasks-widget"
            title="// micro-tasks"
            accent="#fb7185"
            items={tasks}
            render={(t) => (
              <div key={t.id} data-testid={`task-${t.id}`} className="border border-zinc-800 hover:border-[#fb7185]/40 p-3 transition-colors">
                <div className="flex items-baseline justify-between gap-2">
                  <a href={safeUrl(t.url)} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-white hover:text-[#fb7185] truncate">{t.title}</a>
                  <span className="font-mono text-xs neon-green whitespace-nowrap">{t.payout}</span>
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-200">{t.platform} · {t.time}</div>
                  <button
                    data-testid={`save-task-${t.id}`}
                    onClick={() => saveCard("task", t)}
                    className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 hover:text-[#fb7185]"
                  >+ save</button>
                </div>
              </div>
            )}
          />

          <SavedWidget saved={saved} onDel={del} onUpdate={refreshSaved} />
          <AlertsWidget onLoaded={applyPrefs} onSaved={applyPrefs} />
        </div>

        <footer className="mt-12 border-t border-zinc-800 pt-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-24 shrink-0 bg-zinc-900 border border-zinc-800 rounded-md flex items-center justify-center p-1 overflow-hidden">
              <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-cash-mvp-2/artifacts/0086jwtl_NC%20Const.Logo%20Editable%20Jul%2017%2C%202026%2C%2012_37_50%20AM.png" alt="NC" className="max-h-full max-w-full object-contain" />
            </div>
            <div className="h-10 w-24 shrink-0 bg-zinc-900 border border-zinc-800 rounded-md flex items-center justify-center p-1 overflow-hidden">
              <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-cash-mvp-2/artifacts/c2mpfljt_image.png" alt="Clark" className="max-h-full max-w-full object-contain" />
            </div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono">NC Construction × Clark Properties</span>
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono">© 2026 · Money/Hub</div>
        </footer>
      </main>
    </div>
  );
}
